import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Leaf, Download, RefreshCw } from 'lucide-react';
import { emissionApi } from '../services/emissionApi';
import { SustainabilityReport } from '../types/emission';
import { ToastContainer } from '../components/common/Toast';
import { useToast } from '../hooks/useToast';
import '../styles/PageLayout.css';

const todayIso = () => new Date().toISOString().substring(0, 10);
const yearStartIso = () => `${new Date().getFullYear()}-01-01`;

const Sustainability: React.FC = () => {
  const { t } = useTranslation(['common']);
  const [from, setFrom] = useState(yearStartIso());
  const [to, setTo] = useState(todayIso());
  const [report, setReport] = useState<SustainabilityReport | null>(null);
  const [loading, setLoading] = useState(false);
  const { toasts, removeToast, showError, showSuccess } = useToast();

  const loadReport = async () => {
    try {
      setLoading(true);
      setReport(await emissionApi.getReport(from, to));
    } catch (e) {
      showError(t('common:sustainability.loadError', 'Fehler beim Laden des Berichts'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReport(); /* initial */ }, []);

  const handleDownload = async (format: 'csv' | 'json' | 'ecocockpit') => {
    try {
      await emissionApi.downloadExport(from, to, format);
      showSuccess(t('common:sustainability.exportSuccess', 'Export erfolgreich'));
    } catch (e) {
      showError(t('common:sustainability.exportError', 'Fehler beim Export'));
    }
  };

  const card: React.CSSProperties = { flex: 1, background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem' };
  const cardVal: React.CSSProperties = { fontSize: '1.5rem', fontWeight: 700, color: '#111827' };
  const cardLbl: React.CSSProperties = { fontSize: '0.8rem', color: '#6b7280' };

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1rem' }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}><Leaf size={22} /> {t('common:sustainability.title', 'Nachhaltigkeit')}</h1>
        <p style={{ color: '#6b7280', margin: '0.25rem 0 0' }}>{t('common:sustainability.subtitle', 'CO₂e-Bilanz der Reiseplanungen')}</p>
      </div>

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#374151' }}>{t('common:sustainability.from', 'Von')}</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#374151' }}>{t('common:sustainability.to', 'Bis')}</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} />
        </div>
        <button className="page-btn-primary" onClick={loadReport} disabled={loading}>
          <RefreshCw size={16} /> {t('common:sustainability.refresh', 'Aktualisieren')}
        </button>
        <div style={{ flex: 1 }} />
        <button className="page-btn-secondary" onClick={() => handleDownload('csv')}><Download size={16} /> CSV</button>
        <button className="page-btn-secondary" onClick={() => handleDownload('json')}><Download size={16} /> JSON</button>
        <button className="page-btn-secondary" onClick={() => handleDownload('ecocockpit')}><Download size={16} /> EcoCockpit</button>
      </div>

      {loading ? (
        <div className="page-loading"><div className="page-spinner" /></div>
      ) : report && (
        <>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <div style={card}><div style={cardVal}>{report.totalCo2eKg.toLocaleString('de-DE')} kg</div><div style={cardLbl}>{t('common:sustainability.totalCo2e', 'Gesamt CO₂e')}</div></div>
            <div style={card}><div style={cardVal}>{report.totalDistanceKm.toLocaleString('de-DE')} km</div><div style={cardLbl}>{t('common:sustainability.totalDistance', 'Gesamtdistanz')}</div></div>
            <div style={card}><div style={cardVal}>{report.planningCount}</div><div style={cardLbl}>{t('common:sustainability.plannings', 'Planungen')}</div></div>
            <div style={card}><div style={cardVal}>{report.segmentsWithoutFactor}</div><div style={cardLbl}>{t('common:sustainability.withoutFactor', 'Ohne Faktor')}</div></div>
          </div>

          <h3>{t('common:sustainability.byVehicleType', 'Nach Verkehrsmittel')}</h3>
          <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
            <table className="page-table">
              <thead><tr>
                <th>{t('common:emissionFactors.vehicleType', 'Verkehrsmittel')}</th>
                <th>{t('common:sustainability.segments', 'Segmente')}</th>
                <th>{t('common:sustainability.distanceKm', 'Distanz (km)')}</th>
                <th>{t('common:sustainability.co2eKg', 'CO₂e (kg)')}</th>
              </tr></thead>
              <tbody>
                {report.byVehicleType.map(v => (
                  <tr key={v.vehicleType}><td>{v.vehicleType}</td><td>{v.segmentCount}</td><td>{v.distanceKm.toLocaleString('de-DE')}</td><td>{v.co2eKg.toLocaleString('de-DE')}</td></tr>
                ))}
                {report.byVehicleType.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', padding: '1rem' }}>{t('common:sustainability.noData', 'Keine Daten')}</td></tr>}
              </tbody>
            </table>
          </div>

          <h3>{t('common:sustainability.byPlanning', 'Nach Planung')}</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="page-table">
              <thead><tr>
                <th>{t('common:sustainability.date', 'Datum')}</th>
                <th>{t('common:name', 'Name')}</th>
                <th>{t('common:sustainability.segments', 'Segmente')}</th>
                <th>{t('common:sustainability.distanceKm', 'Distanz (km)')}</th>
                <th>{t('common:sustainability.co2eKg', 'CO₂e (kg)')}</th>
              </tr></thead>
              <tbody>
                {report.byPlanning.map(p => (
                  <tr key={p.planningId}><td>{p.date}</td><td>{p.name}</td><td>{p.segmentCount}</td><td>{p.distanceKm.toLocaleString('de-DE')}</td><td>{p.co2eKg.toLocaleString('de-DE')}</td></tr>
                ))}
                {report.byPlanning.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1rem' }}>{t('common:sustainability.noData', 'Keine Daten')}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

const inputStyle: React.CSSProperties = { padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem' };

export default Sustainability;
